import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { AccountsService, IAccount, IAccountUser } from '@services/accounts.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { FilesService } from '@services/files.service';
import { distinctUntilChanged, filter, firstValueFrom, Subject, takeUntil } from 'rxjs';

/**
 * Component
 */
@Component({
	selector: 'app-edit-account',
	templateUrl: './edit-account.component.html',
	styleUrls: ['./edit-account.component.scss']
})
export class EditAccountComponent implements OnInit, OnDestroy {

	/** Path to Accounts Images */
	public imagePathAccounts(): string {
		return imagePath("", imgTypes.accounts).toLowerCase()
	}

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** This User */
	public thisUser: IWebAppUser;

	/** Account GUID */
	private accountGUID: string = ""

	/** Account Users */
	public accountUsers: IAccountUser[];

	/** Form Group */
	dialogForm: FormGroup;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Update Image Instantly */
	public numberRND: number = Math.random();

	/** new Image Selected */
	public newImage: boolean;

	/** today */
	public today: Date = new Date();

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param filesService filesService
	 * @param globalFunctions globalFunctions
	 * @param accountsService accountsService
	 * @param authenticationService authenticationService
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditAccountComponent>,
		private filesService: FilesService,
		private globalFunctions: GlobalFunctions,
		private accountsService: AccountsService,
		private authenticationService: AuthenticationService,
		@Inject(MAT_DIALOG_DATA) public dialogData: IAccount,
	) {

		this.authenticationService.currentUser$.pipe(
			distinctUntilChanged(),
			takeUntil(this.finalise)
		).subscribe({
			next: (currentUser: IWebAppUser) => {
				if (currentUser) {
					this.thisUser = currentUser
					if (this.dialogData.GUID != undefined) {
						this.accountGUID = this.dialogData.GUID;
						// Get Users for the account
						this.getAccountUsers(this.accountGUID);
					} else {
						this.accountUsers = <IAccountUser[]>[{
							UserID: this.thisUser.UserID,
							/** Public ID from Auth0 (Sub) */
							Sub: this.thisUser.Sub,
							/** User Name */
							Name: this.thisUser.Name,
							/** Email */
							Email: this.thisUser.Email
						}]
					}

					var expiryDate = new Date(this.dialogData.Expiry);
					// if (expiryDate.getFullYear() < 2000) {
					// 	this.dialogData.Expiry = new Date();
					// }

					this.dialogForm = this.fb.group({
						Address: [this.dialogData.Address || ''],
						Admin: [this.dialogData.Admin || false],
						Default: [this.dialogData.Default || false],
						Description: [this.dialogData.Description || ''],
						Disabled: [this.dialogData.Disabled || false],
						GUID: [this.dialogData.GUID || ''],
						ID: [this.dialogData.ID || 0],
						Name: [this.dialogData.Name || '', Validators.required],
						Owner: [this.dialogData.Owner || ''],
						OwnerID: [this.dialogData.OwnerID || this.thisUser.UserID],
						RemindersEnabled: [this.dialogData.RemindersEnabled || false],
						ReminderInterval: [this.dialogData.ReminderInterval || 0],
						// #35 Add Trial and Expiry fields to the form group
						Trial: [this.dialogData.Trial || false],
						Expiry: [expiryDate || ""],
						Closed: [this.dialogData.Closed || false]
					});
				}
			}
		})
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.newImage && this.file) {
			try {
				const accountGUID = this.accountGUID.toLowerCase();
				const fileName = this.dialogData.ID.toString();
				const location = "accounts";
				await this.filesService.saveImage(accountGUID, fileName, this.file, location); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			} // Optional: handle upload failure (maybe prevent closing the dialog)
			catch (error) { return; }
		}

		var tmpExpiry = this.dialogForm.get('Expiry')!.value;
		if (tmpExpiry instanceof Date) {
			this.dialogForm.get('Expiry')!.setValue(tmpExpiry.getTime());
		}
		//this.dialogForm.setValue({ Expiry: this.dialogForm.get('Expiry')!.value.getTime() })
		this.dialogRef.close(this.dialogForm.value);
	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	/**
	 * New File deteced
	 * @param file File from child component
	 */
	onNewImage(file: File | null): void {

		/** Check for a valid file */
		if (file) {
			this.file = file;
			this.newImage = true
		}
		else {
			this.file = undefined;
			this.newImage = false;
		}
		this.dialogForm.markAsDirty();
	}


	/**
	 * Get List of Account Users
	 * @param accountGUID
	 */
	async getAccountUsers(accountGUID: string) {

		this.accountUsers = []
		let params = {
			"systemAccount": accountGUID,
			"showDisabled": true,
			"IncludeSiteAdmins": true
		}
		try {
			const users = await firstValueFrom(
				this.accountsService.getAccountUsers$(this.accountGUID, params).pipe(
					filter((users) => !!users))
			);
			if (users) { this.accountUsers = <IAccountUser[]>users }
			else { this.accountUsers = [] }

		}
		catch (error) { this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$") }

	}

}